import { useEffect, useReducer, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CSpinner } from "@coreui/react";

import Pagination from "../../components/Pagination";
import RenderTable from "../../components/RenderTable";
import BoxWrapper from "../../components/BoxWrapper";
import DeleteIcon from "../../components/icons/DeleteIcon";
import { colors } from "../../config/theme";
import { general } from "../../locales/general";
import { pluralize } from "../../helpers/general";
import { useQueryParam } from "../../hooks/useQueryParam";
import { useAuth } from "../../hooks/useAuth";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import ActivitiesTable from "./activityTable";
import ActivityIcon from "../../components/icons/ActiviyIcon";
import { useActivitiesQuery } from "../../hooks/Activities/useActivitiesQuery";

export default function Activities() {
  const { isAdmin } = useAuth();
  const { add, addMultiple } = useQueryParam();
  const [searchParams] = useSearchParams();

  const {
    Activities,
    fetchingActivities,
    deleteActivity,
    mutating,
    totalActivities,
    totalPages,
    currentPage,
  } = useActivitiesQuery();

  const [ActivitiesToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  const [searchInput, setSearchInput] = useState(
    searchParams?.get("search") ?? "",
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      const current = searchParams?.get("search") ?? "";
      const next = searchInput.toLowerCase();
      if (next !== current) {
        addMultiple({ search: next, page: "1" });
      }
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleBulkDelete = () => {
    ActivitiesToBeDeleted.ids.forEach((id) => {
      if (!id) return;
      deleteActivity(id);
    });
  };

  return (
    <section>
      <h5 className="fs-4 fw-bold mb-2">Activités</h5>
      <BoxWrapper>
        <div
          className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
          style={{
            borderTopRightRadius: "10px",
            borderTopLeftRadius: "10px",
            alignSelf: "flex-start",
          }}
        >
          <ActivityIcon />
          <p className="fs-5 fw-bold m-0 text-white text-pre">
            {totalActivities || 0} {pluralize("Activité", totalActivities)}
          </p>
        </div>

        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center">
          <div className="d-flex flex-wrap align-items-center gap-2 p-3">
            <Link
              to="/activities/new"
              className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
            >
              + Ajouter une activité
            </Link>

            {isAdmin && (
              <Link
                to="/activities/organize"
                className="w-lg-full p-2 border btn btn-sm btn-primary rounded-sm shadow-secondary text-white"
              >
                Organiser les activités
              </Link>
            )}

            <button
              disabled={mutating || ActivitiesToBeDeleted?.ids.length === 0}
              onClick={handleBulkDelete}
              className="outline-none bg-white btn btn-outline-danger rounded-sm text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
            >
              {!mutating ? (
                <>
                  <DeleteIcon fill={colors.primary} /> Supprimer la sélection
                </>
              ) : (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              )}
            </button>
          </div>

          <div className="search-form">
            <div className="input-field d-flex align-items-center px-2 bg-white">
              <MagnifyingGlassIcon color="gray" width={20} />
              <input
                type="text"
                className="w-50 border-0 outline-none rounded-sm p-2 text-gray fs-6"
                placeholder="Rechercher une activité..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        <RenderTable
          loading={fetchingActivities}
          render={() =>
            Activities?.length > 0 ? (
              <ActivitiesTable
                activities={Activities}
                onDeleteSelect={dispatch}
              />
            ) : (
              <p className="text-center fs-5 my-5 text-muted">
                {general.fr.noData}
              </p>
            )
          }
        />
      </BoxWrapper>

      {totalPages > 1 && (
        <Pagination
          currentPage={+currentPage}
          onPageChange={(page) => {
            add("page", page.toString());
          }}
          siblingCount={0}
          totalCount={totalPages}
          pageSize={10}
        />
      )}
    </section>
  );
}
